import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, auth, API } from "../lib/api";
import { toast } from "sonner";
import BrandHeader from "../components/BrandHeader";
import SessionDetailDialog from "../components/SessionDetailDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui/table";
import { Download, Trash2, LogOut, PlusCircle, RefreshCw, Loader2, FileText, QrCode, Mail, List, RotateCcw, ChevronRight } from "lucide-react";

const SCHOOL_TYPES = ["Primary", "Intermediate", "High School", "Composite/Area School"];
const TERMS = ["Term 1", "Term 2", "Term 3", "Term 4"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [schools, setSchools] = useState([]);
  const [requests, setRequests] = useState([]);
  const [expSchool, setExpSchool] = useState("__all__");
  const [expTerm, setExpTerm] = useState("__all__");
  const [liveCount, setLiveCount] = useState(0);
  const [lastSnapshot, setLastSnapshot] = useState(null);
  const [archivedCount, setArchivedCount] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  // Bail out to login if no token
  useEffect(() => {
    if (!auth.isAuthed()) {
      nav("/admin/login");
      return;
    }
    api.get("/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => nav("/admin/login"));
  }, [nav]);

  const loadAll = useCallback(async () => {
    try {
      const [s, r, a] = await Promise.all([
        api.get("/admin/schools"),
        api.get("/admin/school-requests"),
        api.get("/admin/archived/count"),
      ]);
      setSchools(s.data);
      setRequests(r.data);
      setArchivedCount(a.data.count);
    } catch { /* handled by interceptor */ }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadCounts = useCallback(async () => {
    const params = {};
    if (expSchool !== "__all__") params.school = expSchool;
    if (expTerm !== "__all__") params.term = expTerm;
    try {
      const [c, snap] = await Promise.all([
        api.get("/admin/sessions/count", { params }),
        api.get("/admin/export/last-snapshot", { params }),
      ]);
      setLiveCount(c.data.count);
      setLastSnapshot(snap.data.snapshot);
    } catch { /* noop */ }
  }, [expSchool, expTerm]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const canClearTerm = useMemo(() => {
    if (!lastSnapshot) return false;
    if (lastSnapshot.count !== liveCount) return false;
    return liveCount > 0;
  }, [liveCount, lastSnapshot]);

  const doLogout = () => {
    auth.clear();
    nav("/admin/login");
  };

  const updateSchool = async (id, patch) => {
    try {
      await api.patch(`/admin/schools/${id}`, patch);
      await loadAll();
      toast.success("School updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const deleteSchool = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from the canonical list?`)) return;
    try {
      await api.delete(`/admin/schools/${id}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const approveRequest = async (req, type) => {
    try {
      await api.post(`/admin/school-requests/${req.id}/approve`, {
        school_type: type,
      });
      await loadAll();
      toast.success(`Added "${req.requested_name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const rejectRequest = async (req) => {
    if (!window.confirm(`Reject "${req.requested_name}"? It won't be added to the list.`)) return;
    try {
      await api.post(`/admin/school-requests/${req.id}/reject`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const body = {
        school: expSchool === "__all__" ? null : expSchool,
        term: expTerm === "__all__" ? null : expTerm,
      };
      const resp = await fetch(`${API}/admin/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.getToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
      const blob = await resp.blob();
      const dispo = resp.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(dispo);
      const filename = m ? m[1] : "cpi_export.xlsx";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${resp.headers.get("X-Export-Count") || ""} sessions`);
      await loadCounts();
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const doClearTerm = async () => {
    setClearing(true);
    try {
      const body = {
        school: expSchool === "__all__" ? null : expSchool,
        term: expTerm === "__all__" ? null : expTerm,
        expected_count: liveCount,
      };
      const r = await api.post("/admin/clear-term", body);
      toast.success(`${r.data.archived} sessions moved to soft-archive`);
      setConfirmClearOpen(false);
      await Promise.all([loadCounts(), loadAll()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-20">
      <BrandHeader
        right={
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-neutral-500 hover:text-neutral-800" data-testid="link-teacher-form">
              Teacher form
            </Link>
            <span className="text-xs text-neutral-400 hidden sm:inline" data-testid="admin-email">
              {me?.email}
            </span>
            <Button variant="outline" size="sm" onClick={doLogout} data-testid="btn-logout">
              <LogOut className="w-3.5 h-3.5 mr-1.5" />
              Sign out
            </Button>
          </div>
        }
      />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div>
          <h1 className="font-display text-3xl font-semibold text-neutral-900 tracking-tight">
            Admin & Export
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            CPI<span className="text-[9px] align-super">™</span> Digital Logsheet — schools, review queue, exports, and end-of-term lifecycle.
          </p>
        </div>

        {/* ---------- Quick tools row ---------- */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="section-quick-tools">
          <PaperSessionCard />
          <QrCodeCard />
          <DigestCard />
        </section>

        {/* ---------- Schools ---------- */}
        <section className="rounded-xl bg-white border border-neutral-200 p-4 sm:p-6" data-testid="section-schools">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="font-display text-xl font-semibold text-neutral-900">Schools</h2>
              <p className="text-xs text-neutral-500 mt-1">
                Central list — School Type is set here once, not re-entered per session.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddOpen(true)}
              data-testid="btn-add-school"
            >
              <PlusCircle className="w-4 h-4 mr-1.5" /> Add school
            </Button>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>School</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schools.map((s) => (
                  <TableRow key={s.id} data-testid={`school-row-${s.id}`}>
                    <TableCell className="font-medium">
                      <Link
                        to={`/admin/schools/${encodeURIComponent(s.name)}`}
                        className="text-neutral-900 hover:text-[#2D5A4F] hover:underline inline-flex items-center gap-1"
                        data-testid={`school-link-${s.id}`}
                      >
                        {s.name}
                        <ChevronRight className="w-3.5 h-3.5 opacity-60" />
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={s.school_type}
                        onValueChange={(v) => updateSchool(s.id, { school_type: v })}
                      >
                        <SelectTrigger className="h-9 w-[210px]" data-testid={`school-type-${s.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SCHOOL_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <SchoolCodeEditor school={s} onSave={updateSchool} />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={s.status}
                        onValueChange={(v) => updateSchool(s.id, { status: v })}
                      >
                        <SelectTrigger className="h-9 w-[130px]" data-testid={`school-status-${s.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="draft">Draft</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteSchool(s.id, s.name)}
                        data-testid={`btn-delete-school-${s.id}`}
                        className="text-red-700 hover:text-red-800 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>

        {/* ---------- Requests queue ---------- */}
        <section className="rounded-xl bg-white border border-neutral-200 p-4 sm:p-6" data-testid="section-requests">
          <div className="mb-4">
            <h2 className="font-display text-xl font-semibold text-neutral-900">
              "School not listed" requests
            </h2>
            <p className="text-xs text-neutral-500 mt-1">
              Submitted from the teacher form. Approve to add to the canonical list —
              past sessions submitted under the requested name will be relabeled automatically.
            </p>
          </div>
          {requests.length === 0 ? (
            <div className="text-sm text-neutral-500 py-6 text-center" data-testid="no-requests">
              No pending requests.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requested name</TableHead>
                    <TableHead>Session context</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="text-right">Approve as</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((r) => (
                    <RequestRow
                      key={r.id}
                      req={r}
                      onApprove={approveRequest}
                      onReject={rejectRequest}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        {/* ---------- Export ---------- */}
        <section className="rounded-xl bg-white border border-neutral-200 p-4 sm:p-6" data-testid="section-export">
          <div className="mb-4">
            <h2 className="font-display text-xl font-semibold text-neutral-900">Export CPI data</h2>
            <p className="text-xs text-neutral-500 mt-1">
              Generates a fresh .xlsx snapshot on demand — Tab 1 raw submissions, Tab 2 pillar averages by school + term.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-sm text-neutral-700 mb-1 block">School</Label>
              <Select value={expSchool} onValueChange={setExpSchool}>
                <SelectTrigger className="h-11" data-testid="export-school">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All schools</SelectItem>
                  {schools.map((s) => (
                    <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-neutral-700 mb-1 block">Term</Label>
              <Select value={expTerm} onValueChange={setExpTerm}>
                <SelectTrigger className="h-11" data-testid="export-term">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All terms</SelectItem>
                  {TERMS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between mb-3 text-sm">
            <div>
              Sessions matching filter: <b className="text-neutral-900" data-testid="live-count">{liveCount}</b>
              <button
                onClick={loadCounts}
                className="ml-3 text-xs text-neutral-500 hover:text-neutral-800 inline-flex items-center"
                data-testid="btn-refresh-counts"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </button>
            </div>
            <div className="text-xs text-neutral-500" data-testid="last-snapshot">
              {lastSnapshot
                ? `Last export: ${lastSnapshot.count} sessions · ${new Date(lastSnapshot.created_at).toLocaleString()}`
                : "No export yet for this filter."}
            </div>
          </div>
          <Button
            onClick={doExport}
            disabled={exporting || liveCount === 0}
            data-testid="btn-export"
            className="h-11 w-full bg-[#2D5A4F] hover:bg-[#264d43] text-white"
          >
            {exporting ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating…</>
            ) : (
              <><Download className="w-4 h-4 mr-2" /> Generate export (.xlsx)</>
            )}
          </Button>
        </section>

        {/* ---------- Session Logs (read-only browse) ---------- */}
        <SessionLogsSection schools={schools} />

        {/* ---------- End of Term ---------- */}
        <section className="rounded-xl bg-white border border-neutral-200 p-4 sm:p-6" data-testid="section-eot">
          <div className="mb-3">
            <h2 className="font-display text-xl font-semibold text-neutral-900">End of Term</h2>
            <p className="text-xs text-neutral-500 mt-1">
              Term-cycle lifecycle: export, then clear. Cleared records are soft-archived
              (recoverable for a buffer period) rather than deleted outright.
            </p>
          </div>
          {canClearTerm ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 mb-3" data-testid="eot-ready">
              Ready to clear — last export ({lastSnapshot?.count} sessions) matches what's in the system.
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 mb-3" data-testid="eot-gate">
              Run an export above first — "Clear Term Data" only unlocks once the exported
              count matches what's currently in the system.
            </div>
          )}
          <Button
            onClick={() => setConfirmClearOpen(true)}
            disabled={!canClearTerm || clearing}
            data-testid="btn-clear-term"
            className="h-11 w-full bg-[#B02A2A] hover:bg-[#8f2222] text-white disabled:opacity-50"
          >
            {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Clear term data (soft-archive)"}
          </Button>
          <p className="text-xs text-neutral-500 mt-3">
            Archived so far: <b className="text-neutral-800" data-testid="archived-count">{archivedCount}</b>
          </p>
        </section>
      </main>

      <AddSchoolDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={loadAll}
      />

      <Dialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
        <DialogContent data-testid="confirm-clear-dialog">
          <DialogHeader>
            <DialogTitle>Clear this term's data?</DialogTitle>
            <DialogDescription>
              This will soft-archive <b>{liveCount}</b> sessions matching your current filter
              ({expSchool === "__all__" ? "all schools" : expSchool}
              {" · "}
              {expTerm === "__all__" ? "all terms" : expTerm}).
              Records move to an archived bucket — they aren't deleted straight away.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClearOpen(false)} data-testid="btn-cancel-clear">
              Cancel
            </Button>
            <Button
              onClick={doClearTerm}
              disabled={clearing}
              data-testid="btn-confirm-clear"
              className="bg-[#B02A2A] hover:bg-[#8f2222] text-white"
            >
              {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, clear term data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RequestRow({ req, onApprove, onReject }) {
  const [type, setType] = useState("Primary");
  return (
    <TableRow data-testid={`request-row-${req.id}`}>
      <TableCell className="font-medium">{req.requested_name}</TableCell>
      <TableCell className="text-sm text-neutral-600">{req.session_summary || "—"}</TableCell>
      <TableCell className="text-xs text-neutral-500">
        {new Date(req.created_at).toLocaleDateString()}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-9 w-[190px]" data-testid={`approve-type-${req.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SCHOOL_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={() => onApprove(req, type)}
            data-testid={`btn-approve-${req.id}`}
            className="bg-[#2D5A4F] hover:bg-[#264d43] text-white"
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onReject(req)}
            data-testid={`btn-reject-${req.id}`}
            className="text-neutral-500 hover:text-red-700"
          >
            Reject
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function AddSchoolDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("Primary");
  const [status, setStatus] = useState("confirmed");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/schools", { name: name.trim(), school_type: type, status });
      toast.success("School added");
      setName("");
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-school-dialog">
        <DialogHeader>
          <DialogTitle>Add a school</DialogTitle>
          <DialogDescription>
            Set the type once here — it's attached to every session logged for this school.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-sm text-neutral-700 mb-1 block">School name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="h-11"
              data-testid="add-school-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-neutral-700 mb-1 block">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-11" data-testid="add-school-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCHOOL_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm text-neutral-700 mb-1 block">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-11" data-testid="add-school-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="btn-add-school-submit"
              className="bg-[#2D5A4F] hover:bg-[#264d43] text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ============================== Quick-tool cards ==============================

function PaperSessionCard() {
  const nav = useNavigate();
  return (
    <div
      className="rounded-xl bg-white border border-neutral-200 p-5 flex flex-col"
      data-testid="card-paper-session"
    >
      <div className="w-10 h-10 rounded-lg bg-[#F0EAE0] flex items-center justify-center mb-3">
        <FileText className="w-5 h-5 text-[#2D5A4F]" />
      </div>
      <h3 className="font-display text-lg font-semibold text-neutral-900">
        Log a paper session
      </h3>
      <p className="text-xs text-neutral-500 mt-1 flex-1">
        Transcribe a physical CPI logsheet into the digital record, with an
        optional photo of the paper original as backup evidence.
      </p>
      <Button
        onClick={() => nav("/admin/paper-session")}
        data-testid="btn-open-paper-session"
        className="mt-4 h-10 bg-[#2D5A4F] hover:bg-[#264d43] text-white"
      >
        Open transcription form
      </Button>
    </div>
  );
}

function QrCodeCard() {
  const [previewing, setPreviewing] = useState(false);
  const qrUrl = `${API}/qr/teacher-form`;
  const teacherUrl = window.location.origin + "/";

  const download = async () => {
    try {
      const resp = await fetch(qrUrl);
      if (!resp.ok) throw new Error("Fetch failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "letssiva-cpi-teacher-form-qr.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("QR downloaded");
    } catch {
      toast.error("Couldn't download QR code");
    }
  };

  return (
    <div
      className="rounded-xl bg-white border border-neutral-200 p-5 flex flex-col"
      data-testid="card-qr"
    >
      <div className="w-10 h-10 rounded-lg bg-[#F0EAE0] flex items-center justify-center mb-3">
        <QrCode className="w-5 h-5 text-[#2D5A4F]" />
      </div>
      <h3 className="font-display text-lg font-semibold text-neutral-900">
        Teacher-form QR code
      </h3>
      <p className="text-xs text-neutral-500 mt-1 flex-1">
        Print & laminate for the gym wall. Scans straight to <span className="break-all text-neutral-700">{teacherUrl}</span> — no login for teachers.
      </p>
      {previewing && (
        <img
          src={qrUrl}
          alt="Teacher form QR"
          className="mt-3 w-40 h-40 rounded-lg border border-neutral-200 self-center"
          data-testid="qr-preview"
        />
      )}
      <div className="mt-4 flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPreviewing((v) => !v)}
          data-testid="btn-toggle-qr-preview"
          className="h-10 flex-1"
        >
          {previewing ? "Hide preview" : "Preview"}
        </Button>
        <Button
          onClick={download}
          data-testid="btn-download-qr"
          className="h-10 flex-1 bg-[#2D5A4F] hover:bg-[#264d43] text-white"
        >
          <Download className="w-4 h-4 mr-1.5" /> PNG
        </Button>
      </div>
    </div>
  );
}

function DigestCard() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const loadPreview = useCallback(async () => {
    try {
      const r = await api.get("/admin/digest/preview");
      setPreview(r.data.data);
    } catch (e) {
      // silent — Preview card just won't show numbers
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const sendNow = async () => {
    if (!window.confirm("Send this week's digest to the admin email right now?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/digest/send-now");
      if (r.data.sent) {
        toast.success(`Digest sent to ${r.data.to}`);
      } else {
        toast.error("Digest generated but email delivery failed — check backend logs.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-xl bg-white border border-neutral-200 p-5 flex flex-col"
      data-testid="card-digest"
    >
      <div className="w-10 h-10 rounded-lg bg-[#F0EAE0] flex items-center justify-center mb-3">
        <Mail className="w-5 h-5 text-[#2D5A4F]" />
      </div>
      <h3 className="font-display text-lg font-semibold text-neutral-900">
        Weekly digest
      </h3>
      <p className="text-xs text-neutral-500 mt-1 flex-1">
        Auto-sent Monday 08:00 (NZ). Summary of the last 7 days plus any pending
        &ldquo;school not listed&rdquo; review items.
      </p>
      {preview && (
        <div className="mt-3 text-xs text-neutral-600 grid grid-cols-3 gap-2" data-testid="digest-preview-stats">
          <div className="text-center bg-[#F7F4EE] rounded-md py-2">
            <div className="text-neutral-900 font-semibold text-base">{preview.n_sessions}</div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-500">Sessions</div>
          </div>
          <div className="text-center bg-[#F7F4EE] rounded-md py-2">
            <div className="text-neutral-900 font-semibold text-base">{preview.n_schools}</div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-500">Schools</div>
          </div>
          <div className="text-center bg-[#F7F4EE] rounded-md py-2">
            <div className="text-[#2D5A4F] font-semibold text-base">{preview.avg_cpi ?? "—"}</div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-500">Avg CPI</div>
          </div>
        </div>
      )}
      <Button
        onClick={sendNow}
        disabled={busy}
        data-testid="btn-send-digest-now"
        className="mt-4 h-10 bg-[#2D5A4F] hover:bg-[#264d43] text-white"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send digest now"}
      </Button>
    </div>
  );
}

// ============================== Session Logs (read-only) ==============================

const MAX_LOGS_DISPLAYED = 200;

function SessionLogsSection({ schools }) {
  const [school, setSchool] = useState("__all__");
  const [term, setTerm] = useState("__all__");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = {};
      if (school !== "__all__") params.school = school;
      if (term !== "__all__") params.term = term;
      if (showDeleted) params.deleted = "true";
      const r = await api.get("/admin/sessions", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load session logs");
    } finally {
      setLoading(false);
    }
  }, [school, term, showDeleted]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === displayed.length) setSelected(new Set());
    else setSelected(new Set(displayed.map((r) => r.id)));
  };

  const displayed = rows.slice(0, MAX_LOGS_DISPLAYED);
  const truncated = rows.length > MAX_LOGS_DISPLAYED;

  const doDelete = async (id) => {
    if (!window.confirm("Soft-delete this session? It will be excluded from lists/exports/averages, and moved to Recently Deleted where you can restore it.")) return;
    try { await api.delete(`/admin/sessions/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const doRestore = async (id) => {
    try { await api.post(`/admin/sessions/${id}/restore`); toast.success("Restored"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };
  const doBulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Soft-delete ${ids.length} session${ids.length === 1 ? "" : "s"}? They'll be recoverable from Recently Deleted.`)) return;
    try { const r = await api.post("/admin/sessions/bulk-delete", { ids }); toast.success(`Deleted ${r.data.deleted}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
  };
  const doBulkRestore = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try { const r = await api.post("/admin/sessions/bulk-restore", { ids }); toast.success(`Restored ${r.data.restored}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bulk restore failed"); }
  };
  const doBulkPurge = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const typed = window.prompt(`Permanently DELETE ${ids.length} session${ids.length === 1 ? "" : "s"}? This cannot be undone. Type DELETE to confirm.`);
    if (typed !== "DELETE") return;
    try { const r = await api.post("/admin/sessions/bulk-purge", { ids }); toast.success(`Purged ${r.data.purged}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bulk purge failed"); }
  };

  return (
    <section className="rounded-xl bg-white border border-neutral-200 p-4 sm:p-6" data-testid="section-session-logs">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-neutral-900 flex items-center gap-2">
            <List className="w-5 h-5 text-[#2D5A4F]" />
            Session Logs
          </h2>
          <p className="text-xs text-neutral-500 mt-1">
            Click a row for the full session detail. Use the checkboxes for bulk delete/restore. Toggle Recently Deleted to see soft-deleted rows.
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs text-neutral-500 hover:text-neutral-800 inline-flex items-center shrink-0"
          data-testid="btn-logs-refresh"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 items-end">
        <div>
          <Label className="text-sm text-neutral-700 mb-1 block">School</Label>
          <Select value={school} onValueChange={setSchool}>
            <SelectTrigger className="h-11" data-testid="logs-school"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All schools</SelectItem>
              {schools.map((s) => (<SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm text-neutral-700 mb-1 block">Term</Label>
          <Select value={term} onValueChange={setTerm}>
            <SelectTrigger className="h-11" data-testid="logs-term"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All terms</SelectItem>
              {["Term 1","Term 2","Term 3","Term 4"].map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <button
            onClick={() => setShowDeleted((v) => !v)}
            data-testid="btn-toggle-deleted"
            className={
              "h-11 w-full px-4 rounded-md border text-sm font-medium " +
              (showDeleted
                ? "bg-red-50 border-red-300 text-red-800"
                : "bg-white border-neutral-300 text-neutral-700 hover:border-neutral-500")
            }
          >
            {showDeleted ? "Showing: Recently Deleted" : "Show Recently Deleted"}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-2 text-sm">
        <div data-testid="logs-count">
          {loading ? "Loading…" : (<><b className="text-neutral-900">{rows.length}</b> session{rows.length === 1 ? "" : "s"}{showDeleted ? " (deleted)" : ""}{truncated && <> · showing latest {MAX_LOGS_DISPLAYED}</>}</>)}
        </div>
        {selected.size > 0 && (
          <div className="flex items-center gap-2" data-testid="bulk-actions">
            <span className="text-neutral-600 text-xs">{selected.size} selected</span>
            {showDeleted ? (
              <>
                <button onClick={doBulkRestore} data-testid="btn-bulk-restore" className="h-9 px-3 rounded-md border border-emerald-300 bg-white text-emerald-800 text-xs font-medium hover:bg-emerald-50 inline-flex items-center gap-1">
                  <RotateCcw className="w-3.5 h-3.5" /> Restore selected
                </button>
                <button onClick={doBulkPurge} data-testid="btn-bulk-purge" className="h-9 px-3 rounded-md border border-red-300 bg-white text-red-800 text-xs font-medium hover:bg-red-50 inline-flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Purge forever
                </button>
              </>
            ) : (
              <button onClick={doBulkDelete} data-testid="btn-bulk-delete" className="h-9 px-3 rounded-md border border-red-300 bg-white text-red-800 text-xs font-medium hover:bg-red-50 inline-flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5" /> Delete selected
              </button>
            )}
          </div>
        )}
      </div>

      {rows.length === 0 && !loading ? (
        <div className="text-sm text-neutral-500 py-8 text-center" data-testid="logs-empty">
          No sessions match the current filter.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === displayed.length}
                    onChange={toggleAll}
                    data-testid="logs-select-all"
                    className="w-4 h-4 accent-[#2D5A4F] cursor-pointer"
                    aria-label="Select all"
                  />
                </TableHead>
                <TableHead className="whitespace-nowrap">School</TableHead>
                <TableHead className="whitespace-nowrap">Year · Class</TableHead>
                <TableHead className="whitespace-nowrap">Teacher</TableHead>
                <TableHead className="whitespace-nowrap">Term · Week</TableHead>
                <TableHead className="whitespace-nowrap text-right">Attendance</TableHead>
                <TableHead className="whitespace-nowrap text-right">CPI Total</TableHead>
                <TableHead className="whitespace-nowrap">Source</TableHead>
                <TableHead className="whitespace-nowrap">Submitted</TableHead>
                <TableHead className="whitespace-nowrap text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayed.map((r) => (
                <SessionLogRow
                  key={r.id}
                  r={r}
                  selected={selected.has(r.id)}
                  onToggleSelect={toggleSelect}
                  onOpen={setDetailId}
                  onDelete={doDelete}
                  onRestore={doRestore}
                  showDeleted={showDeleted}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <SessionDetailDialog
        sessionId={detailId}
        onClose={() => setDetailId(null)}
        onDelete={showDeleted ? undefined : async (id) => { await doDelete(id); }}
        onRestore={showDeleted ? async (id) => { await doRestore(id); } : undefined}
      />
    </section>
  );
}

function SessionLogRow({ r, selected, onToggleSelect, onOpen, onDelete, onRestore, showDeleted }) {
  const attInvalid = r.attendance_status !== "Valid";
  const cpiDisplay = r.cpi_total == null ? "N/A" : `${r.cpi_total}/100`;
  const attDisplay = attInvalid
    ? "Invalid"
    : `${r.attendance_percent}% (${r.present_learners}/${r.expected_on_roll})`;
  let submitted = "—";
  try {
    submitted = new Date(r.created_at).toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { /* fall through */ }

  const isPaper = r.source === "paper_transcription";
  const stop = (e) => e.stopPropagation();

  return (
    <TableRow data-testid={`log-row-${r.id}`} className="cursor-pointer hover:bg-neutral-50" onClick={() => onOpen(r.id)}>
      <TableCell className="align-top w-8" onClick={stop}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(r.id)}
          data-testid={`log-select-${r.id}`}
          className="w-4 h-4 accent-[#2D5A4F] cursor-pointer"
          aria-label={`Select session ${r.id}`}
        />
      </TableCell>
      <TableCell className="align-top">
        <div className="font-medium text-neutral-900">{r.school_name}</div>
        {r.school_type && <div className="text-[11px] text-neutral-500">{r.school_type}</div>}
      </TableCell>
      <TableCell className="align-top whitespace-nowrap">
        <div className="text-neutral-800">{r.year_level}</div>
        <div className="text-[11px] text-neutral-500">Class {r.class_name}</div>
      </TableCell>
      <TableCell className="align-top">
        <div className="text-neutral-800">{r.teacher_name}</div>
        <div className="text-[11px] text-neutral-500">{r.role}</div>
      </TableCell>
      <TableCell className="align-top whitespace-nowrap">
        <div className="text-neutral-800">{r.term}</div>
        <div className="text-[11px] text-neutral-500">{r.session_week}{r.date ? ` · ${r.date}` : ""}</div>
      </TableCell>
      <TableCell
        className={`align-top text-right whitespace-nowrap ${attInvalid ? "text-amber-800" : "text-neutral-900"}`}
        data-testid={`log-attendance-${r.id}`}
      >
        {attDisplay}
      </TableCell>
      <TableCell
        className={`align-top text-right whitespace-nowrap font-semibold ${r.cpi_total == null ? "text-neutral-400" : "text-[#2D5A4F]"}`}
        data-testid={`log-cpi-${r.id}`}
      >
        {cpiDisplay}
        {r.cpi_total != null && (
          <div className="text-[10px] font-normal text-neutral-500">
            {r.engagement_score}/{r.behaviour_score}/{r.collective_energy_score}
          </div>
        )}
      </TableCell>
      <TableCell className="align-top">
        <span
          className={
            "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium " +
            (isPaper
              ? "border-amber-300 bg-amber-50 text-amber-900"
              : "border-emerald-300 bg-emerald-50 text-emerald-900")
          }
          data-testid={`log-source-${r.id}`}
        >
          {isPaper ? "Paper" : "Digital"}
        </span>
      </TableCell>
      <TableCell
        className="align-top whitespace-nowrap text-neutral-700 text-xs"
        data-testid={`log-submitted-${r.id}`}
      >
        {submitted}
      </TableCell>
      <TableCell className="align-top text-right whitespace-nowrap" onClick={stop}>
        {showDeleted ? (
          <button
            onClick={() => onRestore(r.id)}
            className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900"
            data-testid={`log-restore-${r.id}`}
          >
            <RotateCcw className="w-3.5 h-3.5" /> Restore
          </button>
        ) : (
          <button
            onClick={() => onDelete(r.id)}
            className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-900"
            data-testid={`log-delete-${r.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        )}
      </TableCell>
    </TableRow>
  );
}

function SchoolCodeEditor({ school, onSave }) {
  const [value, setValue] = useState(school.school_code || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(school.school_code || ""); }, [school.school_code]);

  const commit = async () => {
    const next = value.trim().toUpperCase();
    if (next === (school.school_code || "")) return;
    setSaving(true);
    try {
      await onSave(school.id, { school_code: next });
    } finally { setSaving(false); }
  };

  return (
    <input
      value={value}
      onChange={(e) => setValue(e.target.value.toUpperCase())}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      placeholder="—"
      maxLength={8}
      disabled={saving}
      data-testid={`school-code-${school.id}`}
      className="h-9 w-24 px-2 rounded-md border border-neutral-300 text-sm font-mono uppercase text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-[#2D5A4F]/30"
    />
  );
}

